import type { DeprecatedToggleOption } from "@v1/components/designSystem/toggle/ToggleGroup";
import type { CustomPropDefinitionAndValue } from "@v1/components/editor/page/element-editor/components/config-menus/utils";
import type { ComponentActionType } from "@v1/types/component-action-type";
import type { ComponentDataMapping } from "replo-runtime/shared/Component";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { Component } from "schemas/component";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import {
  Group,
  GroupHeader,
  GroupTitle,
  GroupTitleContainer,
} from "@v1/components/common/designSystem/Group";
import SelectableButtons from "@v1/components/designSystem/selectable-buttons/SelectableButtons";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import { FieldWithDescription } from "@v1/components/editor/customProp/FieldWithDescription";
import StandardCustomPropControl from "@v1/components/editor/page/element-editor/components/config-menus/StandardCustomPropControl";
import {
  customPropHasType,
  getOptionsFromCustomPropDefinition,
  useCustomPropDefinitionsWithValues,
} from "@v1/components/editor/page/element-editor/components/config-menus/utils";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectComponentDataMapping,
  selectDraftComponent,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import AutoWidth from "@v1/svg/carousel-auto-width";
import FixedWidth from "@v1/svg/carousel-fixed-width";
import { isComponentInsideProductComponent } from "replo-runtime/shared/Component";
import { getDynamicDataBreadcrumbs } from "replo-runtime/shared/utils/dynamic-data";
import {
  exhaustiveSwitch,
  hasOwnProperty,
  isNotNullish,
} from "replo-utils/lib/misc";

import {
  DynamicDataSelector,
  useDynamicData,
} from "../../../DynamicDataSelector";

const OneToFourButtons: React.FC<{
  active: number | undefined;
  setActive: (value: number) => void;
}> = ({ active, setActive }) => {
  const values = [1, 2, 3, 4];
  const noneAreActive = values.includes(active as number) === false;
  return (
    <div className="flex flex-row gap-1 pt-1">
      {[1, 2, 3, 4].map((value) => {
        const isActive = active === value;

        // TODO (Chance 2024-01-24): Implements roving tabindex for toggle
        // buttons. This should be a DS primitive.
        let tabIndex = -1;
        if (isActive || (noneAreActive && value === 1)) {
          tabIndex = 0;
        }
        return (
          <button
            tabIndex={tabIndex}
            type="button"
            aria-pressed={isActive}
            className={twMerge(
              "h-6 w-6 rounded-sm",
              isActive ? "bg-primary text-white" : "bg-surface",
            )}
            key={value}
            onClick={() => setActive(value)}
            onKeyDown={(event) => {
              // TODO (Chance 2024-01-24): Implements keyboard controls for
              // toggle buttons. This should be a DS primitive.
              const preventScroll = () => event.preventDefault();
              const getButtonElement = (value: number) => {
                const currentTarget = event.currentTarget;
                const parentElement = currentTarget.parentElement!;
                const buttons = Array.from(
                  parentElement.children,
                ) as HTMLButtonElement[];
                return buttons[value - 1];
              };
              switch (event.key) {
                case " ":
                case "Enter":
                case "ArrowDown":
                case "ArrowUp":
                  preventScroll();
                  break;
                case "Home":
                  preventScroll();
                  setActive(1);
                  getButtonElement(1)?.focus();
                  break;
                case "End":
                  preventScroll();
                  setActive(4);
                  getButtonElement(4)?.focus();
                  break;
                case "ArrowLeft":
                  preventScroll();
                  const prev = value === 1 ? value : value - 1;
                  setActive(prev);
                  getButtonElement(prev)?.focus();
                  break;
                case "ArrowRight":
                  preventScroll();
                  const next = value === 4 ? value : value + 1;
                  setActive(next);
                  getButtonElement(next)?.focus();
                  break;
                default:
                  return;
              }
            }}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
};

const SlideStyleSelector: React.FC<{
  isAutoWidth: boolean | undefined;
  itemsPerPanel: number | undefined;
  slidesPerMove?: number; // NOTE (Evan, 7/27/23) This is optional b/c we don't use it for the carousel slides menu
  activeAreaProp: CustomPropDefinitionAndValue | undefined;
  setProp: (
    prop:
      | { _autoWidth: boolean; _itemsPerView?: number }
      | { _itemsPerView: number }
      | { _itemsPerMove: number }
      | { _activeArea: string },
  ) => void;
  // Note (Evan, 2024-06-19): This is defined when we are editing the carousel component itself, and
  // undefined when we are editing the carousel slides component (which does not have device-specific overrides)
  activeCanvas?: EditorCanvas;
}> = ({
  isAutoWidth,
  itemsPerPanel,
  slidesPerMove,
  activeAreaProp,
  setProp,
  activeCanvas,
}) => {
  return (
    <Group
      name="Slide Style"
      header={
        <GroupTitleContainer>
          <GroupHeader>
            <GroupTitle>
              {({ name }) => (
                <>
                  <span>{name}</span>
                  {activeCanvas && (
                    <div className="text-muted inline font-normal">
                      <span aria-hidden className="px-1">
                        ·
                      </span>
                      <Tooltip
                        content="These settings can be configured separately on different device sizes"
                        triggerAsChild
                      >
                        <button type="button" tabIndex={0}>
                          <span className="capitalize">{activeCanvas}</span>
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </>
              )}
            </GroupTitle>
          </GroupHeader>
        </GroupTitleContainer>
      }
      isDefaultOpen
      isCollapsible
    >
      <div className="flex w-full gap-2 px-2">
        <div
          className={twMerge(
            "flex w-1/2 cursor-pointer flex-col items-center gap-1 rounded-sm border-2 p-1 text-xs font-medium",
            isAutoWidth === false
              ? "bg-sky-soft border-primary text-primary"
              : "bg-surface text-muted border-transparent",
          )}
          onClick={() => {
            setProp({
              _autoWidth: false,
              _itemsPerView: itemsPerPanel == null ? 1 : itemsPerPanel,
            });
          }}
        >
          <FixedWidth isActive={isAutoWidth === false} />
          <span>Fixed</span>
        </div>
        <div
          className={twMerge(
            "flex w-1/2 cursor-pointer flex-col items-center gap-1 rounded-sm border-2 p-1 text-xs font-medium",
            isAutoWidth === true
              ? "bg-sky-soft border-primary text-primary"
              : "bg-surface text-muted border-transparent",
          )}
          onClick={() => setProp({ _autoWidth: true })}
        >
          <AutoWidth isActive={isAutoWidth === true} />
          <span>Auto</span>
        </div>
      </div>
      {isAutoWidth === false && (
        <div className="text-muted flex w-full flex-row text-xs">
          <div className="w-1/2 p-2">
            <span>Items per Panel</span>
            <OneToFourButtons
              active={itemsPerPanel}
              setActive={(value) => setProp({ _itemsPerView: value })}
            />
          </div>
          {slidesPerMove !== undefined && (
            <div className="w-1/2 p-2">
              <span>Slides per Move</span>
              <div className="flex flex-row gap-1">
                <OneToFourButtons
                  active={slidesPerMove}
                  setActive={(value) => setProp({ _itemsPerMove: value })}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <div className="pl-2 pt-2">
        <span className="text-muted text-xs">Active Slide</span>
        <SelectableButtons
          options={
            activeAreaProp?.definition
              ? getOptionsFromCustomPropDefinition(activeAreaProp.definition) ??
                []
              : []
          }
          size="sm"
          value={[activeAreaProp?.value].filter(isNotNullish)}
          onChange={(newValue) => setProp({ _activeArea: newValue[0] })}
          multiSelect={false}
        />
      </div>
    </Group>
  );
};

const AutoScrollSelector: React.FC<{
  draftComponent: Component;
  componentDataMapping: ComponentDataMapping;
  autoScrollProp: CustomPropDefinitionAndValue;
  pauseOnHoverProp?: CustomPropDefinitionAndValue;
  selectedItemProp: CustomPropDefinitionAndValue;
}> = ({
  draftComponent,
  componentDataMapping,
  autoScrollProp,
  pauseOnHoverProp,
  selectedItemProp,
}) => {
  const [valueTabSelected, setValueTabSelected] = React.useState(false);

  const timerIsActive =
    customPropHasType(autoScrollProp.value, "string") &&
    autoScrollProp.value !== "0s";
  const selectedItemIsActive =
    isNotNullish(selectedItemProp.value) || valueTabSelected;

  const isInsideProductComponent = isComponentInsideProductComponent(
    draftComponent.id,
    componentDataMapping,
  );

  let toggleValue = "none";
  if (timerIsActive) {
    toggleValue = "timer";
  }
  if (selectedItemIsActive) {
    toggleValue = "value";
  }

  const applyComponentAction = useApplyComponentAction();

  const TOGGLE_OPTIONS: DeprecatedToggleOption[] = [
    { value: "none", label: "None" },
    { value: "timer", label: "On Timer" },
  ];
  if (isInsideProductComponent) {
    TOGGLE_OPTIONS.push({ value: "value", label: "To Value" });
  }

  const onToggleGroupChange = (value: "none" | "timer" | "value") => {
    if (value === "none") {
      setValueTabSelected(false);
      applyComponentAction({
        type: "applyCompositeAction",
        value: [
          { type: "setProps", value: { _selectedItem: null } },
          { type: "setProps", value: { _autoNextInterval: "0s" } },
        ],
      });
    } else if (value === "timer") {
      setValueTabSelected(false);
      applyComponentAction({
        type: "applyCompositeAction",
        value: [
          { type: "setProps", value: { _selectedItem: null } },
          { type: "setProps", value: { _autoNextInterval: "1s" } },
        ],
      });
    } else {
      setValueTabSelected(true);
      applyComponentAction({
        type: "applyCompositeAction",
        value: [
          {
            type: "setProps",
            value: { _autoNextInterval: "0s" },
          },
          {
            type: "setProps",
            value: {
              _selectedItem: isInsideProductComponent
                ? `{{attributes._variant.featuredImage}}`
                : null,
            },
          },
        ],
      });
    }
  };

  const dynamicDataDisplayName = selectedItemProp.value
    ? getDynamicDataBreadcrumbs(selectedItemProp.value).toLowerCase()
    : undefined;

  return customPropHasType(autoScrollProp.value, "string") ? (
    <Group name="Autoscroll" isDefaultOpen isCollapsible>
      <DeprecatedToggleGroup
        size="sm"
        selectedValue={toggleValue}
        options={TOGGLE_OPTIONS}
        onChange={(value) => {
          return onToggleGroupChange(value as "none" | "timer" | "value");
        }}
      />
      {timerIsActive && (
        <div className="pt-3">
          <div className="text-muted text-xs">Duration until next slide</div>
          <StandardCustomPropControl
            draftComponent={draftComponent}
            customProp={autoScrollProp}
            noWrapper
          />
          {pauseOnHoverProp && (
            <SwitchWithDescription
              label="Pause on Hover"
              size="sm"
              layoutClassName="w-full mt-4"
              isOn={pauseOnHoverProp.value}
              onChange={(newValue: boolean) => {
                applyComponentAction({
                  type: "setProps",
                  value: { _pauseOnHover: newValue },
                });
              }}
            />
          )}
        </div>
      )}
      {selectedItemIsActive && (
        <div className="pt-3">
          <StandardCustomPropControl
            customProp={selectedItemProp}
            draftComponent={draftComponent}
            noWrapper
            description={
              dynamicDataDisplayName
                ? `When the ${dynamicDataDisplayName} changes, this carousel will scroll to it.`
                : undefined
            }
          />
        </div>
      )}
    </Group>
  ) : null;
};

const AnimationStyleSelector: React.FC<{
  draftComponent: Component;
  animationStyleProp?: CustomPropDefinitionAndValue;
}> = ({ draftComponent, animationStyleProp }) => {
  return (
    <div>
      <div className="text-muted pb-2 text-xs">Animation</div>
      <StandardCustomPropControl
        draftComponent={draftComponent}
        customProp={animationStyleProp}
        noWrapper
      />
    </div>
  );
};

// TODO (Evan, 8/10/23) It's probably worth adding proper type checking here
const DynamicItemsSelector: React.FC<{
  dynamicItemsProp?: CustomPropDefinitionAndValue;
}> = ({ dynamicItemsProp }) => {
  const applyComponentAction = useApplyComponentAction();
  if (!dynamicItemsProp) {
    return null;
  }

  const initialPath = dynamicItemsProp.value?.dynamicPath?.split(".") ?? [];
  return (
    <Group name="Dynamic Data" isDefaultOpen isCollapsible>
      <FieldWithDescription
        description={dynamicItemsProp?.definition.description}
      >
        <DynamicDataSelector
          side="left"
          sideOffset={14}
          targetType="imageList"
          initialPath={initialPath}
          trigger={
            <DynamicDataValueIndicator
              type="other"
              templateValue={initialPath.join(".")}
              onRemove={() => {
                applyComponentAction({
                  type: "deleteProps",
                  propName: dynamicItemsProp.definition.id,
                });
              }}
            />
          }
          onChange={(value) => {
            const transformedValue = {
              type: "inline",
              valueType: "dynamic",
              dynamicPath: value,
            };

            applyComponentAction({
              type: "setProps",
              value: { [dynamicItemsProp.definition.id]: transformedValue },
            });
          }}
        />
      </FieldWithDescription>
    </Group>
  );
};

const shouldDisplaySelectedDynamicItem = (component: Component) => {
  const items = component.props._items;
  if (items) {
    if (
      items.type === "inline" &&
      (!hasOwnProperty(items, "valueType") ||
        (items.valueType === "string" && (items.values?.length ?? 0) === 0))
    ) {
      return false;
    }
    return true;
  }
  return false;
};

type SetPropsPayload = (ComponentActionType & { type: "setProps" })["value"];

/**
 * Returns a setProps action payload that either sets the prop directly (if
 * editing in desktop mode) or sets the prop as an override.
 */
const getOverridableSetPropsAction = (
  prop: SetPropsPayload,
  activeCanvas: EditorCanvas,
) => {
  return exhaustiveSwitch({ type: activeCanvas })({
    desktop: () => prop,
    tablet: () => ({ overrides: { md: prop } }),
    mobile: () => ({ overrides: { sm: prop } }),
  });
};

const getPropById = (
  customProps: CustomPropDefinitionAndValue[],
  id: string,
) => {
  return customProps.find((customProp) => customProp.definition.id === id);
};

const getPropValueById = (
  customProps: CustomPropDefinitionAndValue[],
  id: string,
) => {
  return getPropById(customProps, id)?.value;
};

export const CarouselConfigMenu: React.FC = () => {
  const draftComponent = useEditorSelector(selectDraftComponent);
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const componentDataMapping = useEditorSelector(selectComponentDataMapping);
  const customProps = useCustomPropDefinitionsWithValues({ activeCanvas });
  const applyComponentAction = useApplyComponentAction();
  const { populatedTree: possibleDynamicDataAttributes } = useDynamicData({
    targetType: "imageList",
  });
  if (!draftComponent) {
    return null;
  }

  const isAutoWidth = getPropValueById(customProps, "_autoWidth");
  const itemsPerPanel = getPropValueById(customProps, "_itemsPerView");
  const slidesPerMove = getPropValueById(customProps, "_itemsPerMove");
  const isMouseScrollEnabled = getPropValueById(customProps, "_mouseWheel");
  const isSlideClickTransitionEnabled = getPropValueById(
    customProps,
    "_slideClickTransition",
  );

  const activeAreaProp = getPropById(customProps, "_activeArea");
  const autoScrollProp = getPropById(customProps, "_autoNextInterval");
  const selectedItemProp = getPropById(customProps, "_selectedItem");
  const animationStyleProp = getPropById(customProps, "_animationStyle");
  const infiniteRepeatProp = getPropById(customProps, "_infinite");

  const existingItemsPropIsDynamic = Boolean(
    getPropById(customProps, "_items")?.value,
  );
  const shouldShowDynamicDataSelector = Boolean(
    possibleDynamicDataAttributes?.[0] || existingItemsPropIsDynamic,
  );

  return (
    <div className="relative flex flex-col gap-4">
      {shouldShowDynamicDataSelector && (
        <>
          <DynamicItemsSelector
            dynamicItemsProp={getPropById(customProps, "_items")}
          />
          {shouldDisplaySelectedDynamicItem(draftComponent) && (
            <StandardCustomPropControl draftComponent={draftComponent} />
          )}
          <hr className="border-0.5 border-border" />
        </>
      )}
      {customPropHasType(isAutoWidth, "boolean") &&
        customPropHasType(itemsPerPanel, "integer") &&
        customPropHasType(slidesPerMove, "integer") &&
        activeAreaProp && (
          <SlideStyleSelector
            isAutoWidth={isAutoWidth}
            setProp={(prop) => {
              const propToSet = getOverridableSetPropsAction(
                prop,
                activeCanvas,
              );
              applyComponentAction({
                type: "setProps",
                value: propToSet,
              });
            }}
            itemsPerPanel={itemsPerPanel}
            slidesPerMove={slidesPerMove}
            activeAreaProp={activeAreaProp}
            activeCanvas={activeCanvas}
          />
        )}
      <hr className="border-0.5 border-border" />
      {autoScrollProp && selectedItemProp && (
        <AutoScrollSelector
          draftComponent={draftComponent}
          componentDataMapping={componentDataMapping}
          autoScrollProp={autoScrollProp}
          pauseOnHoverProp={getPropById(customProps, "_pauseOnHover")}
          selectedItemProp={selectedItemProp}
        />
      )}
      <hr className="border-0.5 border-border" />
      <Group name="Transitions" isDefaultOpen isCollapsible>
        <div className="mt-1 flex flex-col gap-4">
          <AnimationStyleSelector
            draftComponent={draftComponent}
            animationStyleProp={animationStyleProp}
          />
          {infiniteRepeatProp && (
            <SwitchWithDescription
              label="Infinite Repeat Slides"
              size="sm"
              layoutClassName="w-full"
              isOn={infiniteRepeatProp.value}
              onChange={(newValue) => {
                return applyComponentAction({
                  type: "setProps",
                  value: { _infinite: newValue },
                });
              }}
            />
          )}
          {customPropHasType(isMouseScrollEnabled, "boolean") && (
            <SwitchWithDescription
              label="Enable scroll with mouse"
              size="sm"
              layoutClassName="w-full"
              isOn={isMouseScrollEnabled}
              onChange={(newValue) => {
                return applyComponentAction({
                  type: "setProps",
                  value: { _mouseWheel: newValue },
                });
              }}
            />
          )}
          {customPropHasType(isSlideClickTransitionEnabled, "boolean") && (
            <SwitchWithDescription
              label="Enable scroll on click"
              size="sm"
              layoutClassName="w-full"
              isOn={isSlideClickTransitionEnabled}
              onChange={(newValue) => {
                return applyComponentAction({
                  type: "setProps",
                  value: { _slideClickTransition: newValue },
                });
              }}
            />
          )}
        </div>
      </Group>
    </div>
  );
};

export const CarouselSlidesConfigMenu: React.FC = () => {
  const customProps = useCustomPropDefinitionsWithValues();
  const draftComponent = useEditorSelector(selectDraftComponent);
  const applyComponentAction = useApplyComponentAction();
  if (!draftComponent) {
    return null;
  }

  const isAutoWidth = getPropValueById(customProps, "_autoWidth");
  const itemsPerPanel = getPropValueById(customProps, "_itemsPerView");

  const activeAreaProp = getPropById(customProps, "_activeArea");
  const animationStyleProp = getPropById(customProps, "_animationStyle");
  const isSlideClickTransitionEnabled = getPropValueById(
    customProps,
    "_slideClickTransition",
  );

  return (
    <div className="relative flex flex-col gap-4">
      <StandardCustomPropControl
        draftComponent={draftComponent}
        customProp={getPropById(customProps, "_useCustomConfig")}
      />
      {getPropValueById(customProps, "_useCustomConfig") && (
        <>
          <SlideStyleSelector
            isAutoWidth={isAutoWidth}
            setProp={(prop) => {
              return applyComponentAction({
                type: "setProps",
                value: prop,
              });
            }}
            itemsPerPanel={itemsPerPanel}
            activeAreaProp={activeAreaProp}
          />

          <Group name="Transitions" isDefaultOpen isCollapsible>
            <div className="mt-1 flex flex-col gap-4">
              <AnimationStyleSelector
                draftComponent={draftComponent}
                animationStyleProp={animationStyleProp}
              />
              {customPropHasType(isSlideClickTransitionEnabled, "boolean") && (
                <SwitchWithDescription
                  label="Enable scroll on click"
                  size="sm"
                  layoutClassName="w-full"
                  isOn={isSlideClickTransitionEnabled}
                  onChange={(newValue) => {
                    return applyComponentAction({
                      type: "setProps",
                      value: { _slideClickTransition: newValue },
                    });
                  }}
                />
              )}
            </div>
          </Group>
        </>
      )}
    </div>
  );
};
